import { useState } from 'react';
import { Box, Typography, List, Card, CircularProgress } from '@mui/material';
import { AccountBalanceWallet } from '@mui/icons-material';
import TransactionItem from './TransactionItem';
import TransactionDetailsModal from './TransactionDetailsModal';
import TransactionFilters from './TransactionFilters';
import TransactionPagination from './TransactionPagination';
import { useTranslation } from '@/kit/useTranslation';

export default function TransactionHistory({
  transactions = [],
  loading = false,
  filter,
  onFilterChange,
  dateRange,
  onDateRangeChange,
  onRefresh,
  page,
  totalPages,
  onPageChange,
  totalCount,
}) {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { t } = useTranslation();

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedTransaction(null);
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {t('payment.transactions.transactionHistory')}
      </Typography>

      {/* Filters */}
      <TransactionFilters
        filter={filter}
        onFilterChange={onFilterChange}
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        onRefresh={onRefresh}
        loading={loading}
        totalCount={totalCount}
      />

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Transaction List or Empty State */}
      {!loading && (
        <>
          {transactions.length > 0 ? (
            <>
              <List sx={{ p: 0 }}>
                {transactions.map((transaction) => (
                  <Box
                    key={transaction.transactionId}
                    onClick={() => handleTransactionClick(transaction)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TransactionItem transaction={transaction} />
                  </Box>
                ))}
              </List>

              {/* Pagination */}
              <TransactionPagination
                page={page}
                totalPages={totalPages}
                onPageChange={onPageChange}
                totalCount={totalCount}
              />
            </>
          ) : (
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid #e0e0e0',
                textAlign: 'center',
                py: 4,
              }}
            >
              <AccountBalanceWallet sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
              <Typography color="text.secondary">
                {totalCount === 0
                  ? t('payment.transactions.noTransactionsYet')
                  : t('payment.transactions.noMatchingTransactions')}
              </Typography>
            </Card>
          )}
        </>
      )}

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        open={detailsOpen}
        onClose={handleCloseDetails}
        transaction={selectedTransaction}
      />
    </Box>
  );
}
